import React, { useEffect, useState } from 'react';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import Cookies from 'js-cookie';
import axios from 'axios';
import { FaUserTie, FaEnvelope, FaPhone, FaBuilding, FaIdBadge } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import API_BASE_URL from '../../../config/api'
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { MdStar, MdTrendingUp } from 'react-icons/md';

const HRProfile = () => {
    const baseUrl = API_BASE_URL;
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];

    // Recent reviews state (match EmployeeDashboard behavior)
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewsError, setReviewsError] = useState(null);
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            setError('');
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                console.log(response.data)
                setProfile(response.data);
            } catch (err) {
                setError('Failed to load profile.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // Fetch analytics for the employee once profile is loaded
    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!profile?.employee?.id) return;
            setAnalyticsLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params: { employee: profile.employee.id }
                });
                setAnalytics(response.data);
            } catch (err) {
                setAnalytics(null);
            } finally {
                setAnalyticsLoading(false);
            }
        };
        if (profile) {
            fetchAnalytics();
        }
    }, [profile]);

    // Fetch recent reviews like EmployeeDashboard
    useEffect(() => {
        const fetchReviews = async () => {
            setLoadingReviews(true);
            setReviewsError(null);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                const completed = (Array.isArray(response.data.employee.goals) ? response.data.employee.goals : [])
                    .filter(g => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0);
                completed.sort((a, b) => new Date(b.updated_at || b.completed_at || b.created_at) - new Date(a.updated_at || a.completed_at || a.created_at));
                setReviews(completed);
            } catch (err) {
                setReviewsError('Failed to fetch recent reviews.');
            } finally {
                setLoadingReviews(false);
            }
        };
        fetchReviews();
    }, []);

    const stats = analytics?.overall_statistics || {};
    const timeliness = analytics?.overall_statistics || {};

    // Transform quarterly_trends object to array for recharts
    const quarterlyTrendsData = React.useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, stats]) => ({
            quarter,
            ...stats
        }));
    }, [analytics]);

    // RecentEvaluations component (match EmployeeDashboard columns/layout)
    const RecentEvaluations = ({ reviews }) => {
        return (
            <div className="recent-evaluations-card" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: 24, marginTop: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Recent Reviews</h2>
                    <Link
                        to="/hr/my-goal-reviews"
                        style={{
                            background: '#4F46E5',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            padding: '6px 18px',
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            transition: 'background 0.2s',
                            textDecoration: 'none',
                            display: 'inline-block',
                        }}
                    >
                        View All
                    </Link>
                </div>
                {loadingReviews ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>Loading...</div>
                ) : reviewsError ? (
                    <div style={{ color: '#ef4444', textAlign: 'center', padding: 32 }}>{reviewsError}</div>
                ) : (!reviews || reviews.length === 0) ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>No recent reviews found.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f3f4f6' }}>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Activity / KPI</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Performance Standard / Target</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Metric</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>Self Score</th>
                                    <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600, fontSize: 15 }}>HOD Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.slice(0, 5).map((review, idx) => (
                                    <tr key={review.id || idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '10px 8px', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.activity || '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.performance_standard || '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.metric || '-'}</td>
                                        <td style={{ padding: '10px 8px', color: '#4F46E5', fontWeight: 600 }}>{review.score != null ? review.score : '-'}</td>
                                        <td style={{ padding: '10px 8px', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.hod_score != null ? review.hod_score : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Topbar />
            <Sidebar />
            <div className="dashboard-container">
                
                {/* Employee Analytics Section */}
                <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                    <div>
                        <h1>Performance Overview</h1>
                        <p>Overview of your performance metrics and KPIs</p>
                    </div>
                    {loading ? (
                        <div style={{ minWidth: 220, textAlign: 'right' }}><Skeleton width={180} height={32} /></div>
                    ) : error ? null : profile ? (
                        <div style={{ minWidth: 220, textAlign: 'right', background: '#f3f4f6', borderRadius: 12, padding: '12px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                            <div style={{ fontWeight: 700, fontSize: 18, color: '#1e293b', marginBottom: 2 }}>
                                {profile?.employee?.first_name} {profile?.employee?.last_name}
                            </div>
                            <div style={{ color: '#6366f1', fontWeight: 500, fontSize: 15, marginBottom: 2 }}>
                                {profile?.employee?.employee_id ? `Code: ${profile.employee.employee_id}` : ''}
                            </div>
                            <div style={{ color: '#64748b', fontSize: 15 }}>
                                {profile?.employee?.position || 'HR Manager'}
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon"><MdStar /></div>
                        <div className="stat-content">
                            <h3>Completed Goals</h3>
                            <div className="stat-value">{analyticsLoading ? <Skeleton width={60} /> : `${stats.completed_goals ?? 0} / ${stats.total_goals ?? 0}`}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><MdTrendingUp /></div>
                        <div className="stat-content">
                            <h3>Avg Performance</h3>
                            <div className="stat-value">{analyticsLoading ? <Skeleton width={60} /> : stats.average_progress ? `${stats.average_progress}%` : '0%'}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><MdStar /></div>
                        <div className="stat-content">
                            <h3>Goal Completion Timeliness</h3>
                            <div className="stat-value">
                                {analyticsLoading ? <Skeleton width={120} /> : (
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', marginTop: 4 }}>
                                        <span style={{ background: '#22c55e', color: 'white', borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12, lineHeight: '18px' }}>
                                            On Time: {timeliness.on_time_rate != null ? timeliness.on_time_rate : 0}%
                                        </span>
                                        <span style={{ background: '#3b82f6', color: 'white', borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12, lineHeight: '18px' }}>
                                            Early: {timeliness.early_rate != null ? timeliness.early_rate : 0}%
                                        </span>
                                        <span style={{ background: '#ef4444', color: 'white', borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12, lineHeight: '18px' }}>
                                            Late: {timeliness.late_rate != null ? timeliness.late_rate : 0}%
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="charts-grid">
                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>Quarterly Performance Trends</h3>
                        </div>
                        <div className="chart-content">
                            {analyticsLoading ? <Skeleton height={300} /> : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="quarter" />
                                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                        <Tooltip formatter={(value) => `${value}%`} />
                                        <Area 
                                            type="monotone" 
                                            dataKey="completion_rate" 
                                            stroke="#4F46E5" 
                                            fillOpacity={1} 
                                            fill="url(#colorQuarterly)" 
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
                <RecentEvaluations reviews={reviews} />
            </div>
        </>
    );
};

export default HRProfile;
